/**
 * Build a complete recap payload (matching what POST /api/recaps expects) from
 * a post's `scorecard_data` — so posts created BEFORE the recap-summary fields
 * (pars / skins_by_hole / bundle_wins / leaderboard[].scores) existed still
 * produce a full public recap when shared.
 *
 * Accepts an optional legacy `recap` (post.scorecard_data.recap_summary) whose
 * headline fields (low_gross / low_net / most_birdies / ctp / teams) will be
 * preserved and merged into the rebuilt payload.
 */
export function buildRecapPayloadFromPost(scorecardData, legacyRecap) {
  const sc = scorecardData || {};
  const rawPlayers = Array.isArray(sc.players) ? sc.players : [];
  if (rawPlayers.length === 0) return legacyRecap || null;

  // 1. Resolve per-hole pars: prefer root scorecard_data.pars, else union of
  //    players' pars, else course_holes/holes[].par.
  const parByHole = {};
  const courseHoles = Array.isArray(sc.course_holes) ? sc.course_holes : Array.isArray(sc.holes) ? sc.holes : [];
  for (let h = 1; h <= 18; h++) {
    const key = String(h);
    let par = null;
    if (sc.pars && sc.pars[key] != null) par = Number(sc.pars[key]);
    if (par == null || Number.isNaN(par)) {
      for (const p of rawPlayers) {
        if (p?.pars && p.pars[key] != null) { par = Number(p.pars[key]); break; }
      }
    }
    if ((par == null || Number.isNaN(par)) && courseHoles[h - 1]?.par != null) {
      par = Number(courseHoles[h - 1].par);
    }
    if (par != null && !Number.isNaN(par)) parByHole[key] = par;
  }

  const par3Holes = Object.entries(parByHole)
    .filter(([, par]) => Number(par) === 3)
    .map(([h]) => parseInt(h, 10))
    .sort((a, b) => a - b);

  // 2. Per-player stats (front9/back9 totals, gross/net, eagles/birdies/skins)
  const allPlayerSkins = sc.all_player_skins || {}; // keyed by player index
  const playerStats = rawPlayers.map((p, idx) => {
    const scores = p?.scores || {};
    let eagles = 0, birdies = 0, pars = 0, holesPlayed = 0, front9 = 0, back9 = 0, front9Holes = 0, back9Holes = 0;
    const holeScores = {};
    let derivedTotal = 0;
    for (let h = 1; h <= 18; h++) {
      const raw = scores[String(h)];
      const s = raw == null || raw === '' ? null : Number(raw);
      if (s == null || Number.isNaN(s)) continue;
      holeScores[h] = s;
      derivedTotal += s;
      holesPlayed++;
      if (h <= 9) { front9 += s; front9Holes++; } else { back9 += s; back9Holes++; }
      const par = Number(parByHole[String(h)]);
      if (!Number.isNaN(par)) {
        const diff = s - par;
        if (diff <= -2) eagles++;
        else if (diff === -1) birdies++;
        else if (diff === 0) pars++;
      }
    }
    const totalPar = Object.entries(holeScores).reduce((sum, [h]) => sum + (Number(parByHole[String(h)]) || 0), 0);
    const explicitTotal = scores.total != null ? Number(scores.total) : null;
    const total = (explicitTotal != null && !Number.isNaN(explicitTotal)) ? explicitTotal : derivedTotal;
    const handicap = p?.handicap != null ? Number(p.handicap) : null;
    const netTotal = (handicap != null && !Number.isNaN(handicap)) ? total - handicap : null;
    // Skins may come from all_player_skins keyed by index, or from p.skins directly
    const skinsFromMap = allPlayerSkins[idx] ?? allPlayerSkins[String(idx)];
    const skins = Number(skinsFromMap ?? p?.skins ?? 0) || 0;
    return {
      name: p?.name || `Player ${idx + 1}`,
      idx,
      total,
      holes: holesPlayed,
      totalPar,
      scoreToPar: total - totalPar,
      eagles,
      birdies,
      pars,
      skins,
      handicap,
      netTotal,
      front9: front9Holes > 0 ? front9 : null,
      front9Holes,
      back9: back9Holes > 0 ? back9 : null,
      back9Holes,
      holeScores,
    };
  }).filter(p => p.holes > 0 || p.total > 0);

  if (playerStats.length === 0) return legacyRecap || null;

  // 3. Skins by hole (recomputed for accuracy — legacy all_player_skins may not
  //    have hole-level detail)
  const skinsByHole = [];
  for (let h = 1; h <= 18; h++) {
    const scoresThisHole = playerStats
      .map(p => ({ p, s: p.holeScores[h] }))
      .filter(x => x.s != null);
    if (scoresThisHole.length < 2) continue;
    const minScore = Math.min(...scoresThisHole.map(x => x.s));
    const winners = scoresThisHole.filter(x => x.s === minScore);
    if (winners.length === 1) {
      skinsByHole.push({
        hole: h,
        winner_name: winners[0].p.name,
        score: winners[0].s,
        par: parByHole[String(h)] ?? null,
      });
    }
  }

  // 4. Sync each player's skins count from the recomputed skins_by_hole so
  //    downstream "Most Skins" is consistent with what's shown per-hole.
  const recomputedSkinCounts = {};
  skinsByHole.forEach(s => {
    recomputedSkinCounts[s.winner_name] = (recomputedSkinCounts[s.winner_name] || 0) + 1;
  });
  playerStats.forEach(p => {
    if (recomputedSkinCounts[p.name] != null) p.skins = recomputedSkinCounts[p.name];
  });

  // 5. Bundle wins (F9 / B9 / Overall)
  const f9Eligible = playerStats.filter(p => p.front9 != null && p.front9Holes === 9);
  const b9Eligible = playerStats.filter(p => p.back9 != null && p.back9Holes === 9);
  const overall = [...playerStats].sort((a, b) => a.total - b.total)[0];
  const bundleWins = {
    front9: f9Eligible.length > 0
      ? (() => { const w = [...f9Eligible].sort((a, b) => a.front9 - b.front9)[0]; return { name: w.name, total: w.front9 }; })()
      : null,
    back9: b9Eligible.length > 0
      ? (() => { const w = [...b9Eligible].sort((a, b) => a.back9 - b.back9)[0]; return { name: w.name, total: w.back9 }; })()
      : null,
    overall: overall ? { name: overall.name, total: overall.total } : null,
  };

  // 6. Highlight cards — recompute from source scorecard whenever we have
  //    hole-by-hole data (legacy recap headline objects may be stale). CTP is
  //    the only field we always preserve from legacy_recap since it can't be
  //    derived from scorecard alone.
  const lowGross = overall
    ? { name: overall.name, total: overall.total, score_to_par: overall.scoreToPar }
    : (legacyRecap?.low_gross || legacyRecap?.low_score || null);
  const netEligible = playerStats.filter(p => p.netTotal != null);
  const lowNet = netEligible.length > 0
    ? (() => { const w = [...netEligible].sort((a, b) => a.netTotal - b.netTotal)[0]; return { name: w.name, net_total: w.netTotal, handicap: w.handicap, gross: w.total }; })()
    : (legacyRecap?.low_net || null);
  const birdiesLeader = [...playerStats].sort((a, b) => (b.birdies + b.eagles * 2) - (a.birdies + a.eagles * 2))[0];
  const mostBirdies = (birdiesLeader && (birdiesLeader.birdies > 0 || birdiesLeader.eagles > 0))
    ? { name: birdiesLeader.name, birdies: birdiesLeader.birdies, eagles: birdiesLeader.eagles }
    : (legacyRecap?.most_birdies || null);
  const skinsLeader = [...playerStats].sort((a, b) => b.skins - a.skins)[0];
  const mostSkins = (skinsLeader && skinsLeader.skins > 0)
    ? { name: skinsLeader.name, skins: skinsLeader.skins }
    : (legacyRecap?.most_skins || null);

  return {
    course_name: legacyRecap?.course_name || sc.course_name || '',
    date: legacyRecap?.date || sc.date || '',
    low_gross: lowGross,
    low_score: lowGross, // backwards-compat
    low_net: lowNet,
    most_birdies: mostBirdies,
    most_skins: mostSkins,
    ctp: legacyRecap?.ctp || [],
    par3_holes: par3Holes,
    pars: parByHole,
    skins_by_hole: skinsByHole,
    bundle_wins: bundleWins,
    leaderboard: [...playerStats].sort((a, b) => a.total - b.total).map(p => ({
      name: p.name,
      total: p.total,
      score_to_par: p.scoreToPar,
      eagles: p.eagles,
      birdies: p.birdies,
      pars: p.pars,
      skins: p.skins,
      handicap: p.handicap,
      net_total: p.netTotal,
      front9: p.front9,
      back9: p.back9,
      scores: p.holeScores,
    })),
  };
}

/**
 * Returns true when the recap object is missing the fields that PublicRecap
 * needs to render the full experience (scorecard table, skins, bundle wins).
 */
export function recapNeedsRebuild(recap) {
  if (!recap) return true;
  const hasPars = recap.pars && Object.keys(recap.pars).length >= 9;
  const hasBundleWins = recap.bundle_wins && (recap.bundle_wins.front9 || recap.bundle_wins.back9 || recap.bundle_wins.overall);
  const lb = recap.leaderboard || [];
  const hasHoleScores = lb.some(p => p.scores && Object.keys(p.scores).length > 0);
  return !(hasPars && hasBundleWins && hasHoleScores);
}
